/**
 * Heuristic detection of embedded / in-app browsers (email clients, social apps, WebViews).
 * Many use different JS engines or strip features; UAs are inconsistent — this is best-effort only.
 * iOS Mail / Gmail often report as “Mobile Safari” with no reliable Gmail token; chunk handling
 * still relies on broadened `isChunkLoadError` patterns.
 */
export function isLikelyInAppOrEmbeddedBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  const ua = navigator.userAgent || ''

  // Android system WebView (many in-app browsers)
  if (/; wv\)/i.test(ua)) {
    return true
  }

  // Google app shells (Gmail “open link”, Search app, etc.)
  if (/Gmail|GSA\/|GoogleApp\//i.test(ua)) {
    return true
  }

  // Social / messaging in-app browsers
  if (/FBAN|FBAV/i.test(ua)) {
    return true
  }
  if (/Instagram/i.test(ua)) {
    return true
  }
  if (/Line\//i.test(ua)) {
    return true
  }
  if (/Snapchat/i.test(ua)) {
    return true
  }

  return false
}

const IN_APP_TIP_SESSION_KEY = 'gh_in_app_browser_tip_shown'

/**
 * One informational toast per tab session when the user likely opened the app inside another app.
 */
export function showOpenInBrowserTipOncePerSession(): void {
  if (!isLikelyInAppOrEmbeddedBrowser()) {
    return
  }
  try {
    if (sessionStorage.getItem(IN_APP_TIP_SESSION_KEY) === '1') {
      return
    }
    sessionStorage.setItem(IN_APP_TIP_SESSION_KEY, '1')
  } catch {
    return
  }

  const toastInfo = (globalThis as { toastInfo?: (message: string) => void }).toastInfo
  if (typeof toastInfo === 'function') {
    toastInfo(
      'For the best experience, open this page in Safari or Chrome — use the menu and choose “Open in Browser” or similar.',
    )
  }
}
